import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, Tag, Space, message, Popconfirm, Switch } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, KeyOutlined, SettingOutlined, ImportOutlined, MenuOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useMenuPermission } from '../hooks/useMenuPermission';

const FIELD_LABELS = {
  supplier_id: '主体',
  filter_use: '筛选使用',
  platform: '平台',
  handler: '受理员工',
  team: '小组',
  period: '期数',
  group_name: '群名',
  order_time: '下单时间',
  contact_person: '对接人',
  growth_method: '涨粉方式',
  account_name: '视频号名称/抖音号',
  text3: '文本3',
  link_type: '链接属性',
  exclusive_link: '专属链接/涨粉链接',
  account_duplicate: '账号重复',
  method_duplicate: '方式+账号重复',
  initial_fans: '初始粉丝数',
  final_fans: '涨粉后粉丝数',
  growth_count: '涨粉量',
  pre_growth_text: '涨粉前账号截图文字',
  post_growth_text: '涨粉完结截图文字',
  status_id: '完结状态',
  complete_date: '完成日期',
  remark: '备注',
  text1: '文本1',
  text2: '文本2',
  reference_price: '参考价格',
  actual_price: '实际价格',
  modified_by_name: '最新修改人'
};

const MENU_LABELS = {
  '/orders': '订单管理',
  '/dashboard': '数据看板',
  '/settlement/supplier': '按主体结算',
  '/settlement/overall': '整体结算',
  '/settlement/profit': '利润表',
  '/orders/import': '批量导入订单',
  '/suppliers': '主体管理',
  '/users': '用户管理',
  '/permission-templates': '权限关联',
  '/statuses': '状态管理',
  '/teams': '小组管理',
  '/growth-methods': '涨粉方式管理',
  '/platforms': '平台管理',
  '/operation-logs': '操作日志',
  '/daily-dashboard': '每日看板',
  '/yfx': '有效粉',
  '/supplier-prices': '参考价格管理',
  '/fan-monitor': '涨粉监控',
  '/scheduled-tasks': '定时任务'
};

function UserManage() {
  const navigate = useNavigate();
  const { canEdit } = useMenuPermission();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [suppliers, setSuppliers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [resetPwdVisible, setResetPwdVisible] = useState(false);
  const [resetPwdUser, setResetPwdUser] = useState(null);
  const [form] = Form.useForm();
  const [resetForm] = Form.useForm();

  // 字段权限配置
  const [fieldPermVisible, setFieldPermVisible] = useState(false);
  const [fieldPermUser, setFieldPermUser] = useState(null);
  const [fieldPermLoading, setFieldPermLoading] = useState(false);
  const [fieldPermSaving, setFieldPermSaving] = useState(false);
  const [fieldPermissions, setFieldPermissions] = useState([]);

  // 菜单权限配置
  const [menuPermVisible, setMenuPermVisible] = useState(false);
  const [menuPermUser, setMenuPermUser] = useState(null);
  const [menuPermLoading, setMenuPermLoading] = useState(false);
  const [menuPermSaving, setMenuPermSaving] = useState(false);
  const [menuPermissions, setMenuPermissions] = useState([]);

  // 数据权限配置
  const [dataPermVisible, setDataPermVisible] = useState(false);
  const [dataPermUser, setDataPermUser] = useState(null);
  const [dataPermLoading, setDataPermLoading] = useState(false);
  const [dataPermSaving, setDataPermSaving] = useState(false);
  const [dataPermissions, setDataPermissions] = useState({ platforms: [], teams: [], statuses: [] });
  const [allPlatforms, setAllPlatforms] = useState([]);
  const [allTeams, setAllTeams] = useState([]);

  // 账单权限配置
  const [billPermVisible, setBillPermVisible] = useState(false);
  const [billPermUser, setBillPermUser] = useState(null);
  const [billPermLoading, setBillPermLoading] = useState(false);
  const [billPermSaving, setBillPermSaving] = useState(false);
  const [billPermissions, setBillPermissions] = useState([
    { field_name: 'bill_team', label: '小组' },
    { field_name: 'bill_settlement_date', label: '结算日期' },
    { field_name: 'bill_amount', label: '金额' },
    { field_name: 'bill_is_paid', label: '是否打款' },
    { field_name: 'bill_is_invoiced', label: '是否开票' },
    { field_name: 'bill_is_collected', label: '是否回单' },
    { field_name: 'bill_supplier_id', label: '主体' },
    { field_name: 'bill_payee_supplier', label: '打款主体' },
    { field_name: 'bill_pay_supplier', label: '收款主体' },
    { field_name: 'bill_invoice_file', label: '发票上传' },
    { field_name: 'bill_remark', label: '备注' }
  ].map(f => ({ ...f, is_visible: 1, is_editable: 1 })));
  const [allStatuses, setAllStatuses] = useState([]);

  useEffect(() => {
    loadData();
    loadSuppliers();
    loadTemplates();
  }, [page]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/users', { params: { page, pageSize: 20 } });
      if (res.code === 200) {
        setData(res.data.list);
        setTotal(res.data.total);
      }
    } catch (err) {}
    setLoading(false);
  };

  const loadSuppliers = async () => {
    try {
      const res = await api.get('/suppliers/all');
      if (res.code === 200) setSuppliers(res.data);
    } catch (err) {}
  };

  const loadTemplates = async () => {
    try {
      const res = await api.get('/permission-templates');
      if (res.code === 200) setTemplates(res.data);
    } catch (err) {}
  };

  const handleAdd = () => {
    setEditingUser(null);
    form.resetFields();
    form.setFieldsValue({ role: 'supplier', status: 1 });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingUser(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const values = await form.validateFields();
      // 清理主体角色时的 supplier_id
      if (values.role === 'admin') {
        values.supplier_id = null;
      }
      let res;
      if (editingUser) {
        res = await api.put(`/users/${editingUser.id}`, values);
      } else {
        res = await api.post('/users', values);
      }
      if (res.code === 200) {
        message.success(editingUser ? '更新成功' : '创建成功');
        setModalVisible(false);
        loadData();
      }
    } catch (err) {}
    setSaving(false);
  };

  const handleDelete = async (id) => {
    try {
      const res = await api.delete(`/users/${id}`);
      if (res.code === 200) {
        message.success('删除成功');
        loadData();
      }
    } catch (err) {}
  };

  const handleResetPassword = async () => {
    try {
      const values = await resetForm.validateFields();
      const res = await api.post(`/users/${resetPwdUser.id}/reset-password`, {
        newPassword: values.newPassword
      });
      if (res.code === 200) {
        message.success('密码重置成功');
        setResetPwdVisible(false);
      }
    } catch (err) {}
  };

  // 打开字段权限配置
  const handleFieldPerm = async (record) => {
    setFieldPermUser(record);
    setFieldPermLoading(true);
    setFieldPermVisible(true);
    try {
      const res = await api.get(`/users/${record.id}/fields`);
      if (res.code === 200) {
        setFieldPermissions(res.data);
      }
    } catch (err) {}
    setFieldPermLoading(false);
  };

  // 切换字段权限
  const handleFieldToggle = (fieldName, key, checked) => {
    setFieldPermissions(prev =>
      prev.map(f => {
        if (f.field_name !== fieldName) return f;
        const updated = { ...f, [key]: checked };
        if (key === 'is_visible' && !checked) {
          updated.is_editable = false;
        }
        return updated;
      })
    );
  };

  // 全选/全不选
  const handleSelectAll = (key, value) => {
    setFieldPermissions(prev => prev.map(f => ({ ...f, [key]: value })));
  };

  // 保存字段权限
  const handleSaveFieldPerm = async () => {
    setFieldPermSaving(true);
    try {
      const res = await api.put(`/users/${fieldPermUser.id}/fields`, {
        fields: fieldPermissions.map(f => ({
          field_name: f.field_name,
          is_visible: f.is_visible,
          is_editable: f.is_editable
        }))
      });
      if (res.code === 200) {
        message.success('字段权限保存成功');
      }
    } catch (err) {}
    setFieldPermSaving(false);
  };

  // 打开菜单权限配置
  const handleMenuPerm = async (record) => {
    setMenuPermUser(record);
    setMenuPermLoading(true);
    setMenuPermVisible(true);
    try {
      const res = await api.get(`/users/${record.id}/menus`);
      if (res.code === 200) {
        setMenuPermissions(res.data);
      }
    } catch (err) {}
    setMenuPermLoading(false);
  };

  // 切换菜单权限
  const handleMenuToggle = (menuKey, field, checked) => {
    setMenuPermissions(prev =>
      prev.map(m => {
        if (m.key !== menuKey) return m;
        const updated = { ...m, [field]: checked ? 1 : 0 };
        // 不可见时自动不可编辑
        if (field === 'is_visible' && !checked) {
          updated.is_editable = 0;
        }
        return updated;
      })
    );
  };

  // 保存菜单权限
  const handleSaveMenuPerm = async () => {
    setMenuPermSaving(true);
    try {
      const menus = menuPermissions.map(m => ({
        key: m.key,
        is_visible: m.is_visible,
        is_editable: m.is_editable
      }));
      const res = await api.put(`/users/${menuPermUser.id}/menus`, { menus });
      if (res.code === 200) {
        message.success('菜单权限保存成功，用户重新登录后生效');
      }
    } catch (err) {}
    setMenuPermSaving(false);
  };

  // 打开数据权限配置
  const handleDataPerm = async (record) => {
    setDataPermUser(record);
    setDataPermLoading(true);
    setDataPermVisible(true);
    try {
      const [permRes, platformsRes, teamsRes, statusRes] = await Promise.all([
        api.get(`/users/${record.id}/data-permissions`),
        api.get('/platforms'),
        api.get('/teams'),
        api.get('/statuses')
      ]);
      if (permRes.code === 200) {
        setDataPermissions({ platforms: permRes.data.platforms || [], teams: permRes.data.teams || [], statuses: permRes.data.statuses || [] });
      }
      if (platformsRes.code === 200) setAllPlatforms(platformsRes.data || []);
      if (teamsRes.code === 200) setAllTeams(teamsRes.data || []);
      if (statusRes.code === 200) setAllStatuses(statusRes.data || []);
    } catch (err) {}
    setDataPermLoading(false);
  };

  // 保存数据权限
  const handleSaveDataPerm = async () => {
    setDataPermSaving(true);
    try {
      const res = await api.put(`/users/${dataPermUser.id}/data-permissions`, dataPermissions);
      if (res.code === 200) {
        message.success('数据权限保存成功，用户重新登录后生效');
        setDataPermVisible(false);
      }
    } catch (err) {}
    setDataPermSaving(false);
  };

  // 账单权限
  const handleBillPerm = async (record) => {
    setBillPermUser(record); setBillPermLoading(true); setBillPermVisible(true);
    try {
      const res = await api.get(`/users/${record.id}/bill-fields`);
      if (res.code === 200 && res.data?.length > 0) {
        setBillPermissions(prev => prev.map(p => {
          const saved = res.data.find(f => f.field_name === p.field_name);
          return saved ? { ...p, is_visible: saved.is_visible, is_editable: saved.is_editable } : p;
        }));
      } else {
        setBillPermissions(prev => prev.map(p => ({ ...p, is_visible: 1, is_editable: 1 })));
      }
    } catch (e) {}
    setBillPermLoading(false);
  };
  const handleSaveBillPerm = async () => {
    setBillPermSaving(true);
    try {
      await api.put(`/users/${billPermUser.id}/bill-fields`, { fields: billPermissions });
      message.success('账单权限保存成功');
      setBillPermVisible(false);
    } catch (e) {}
    setBillPermSaving(false);
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '用户名', dataIndex: 'username', width: 120 },
    { title: '显示名称', dataIndex: 'name', width: 120 },
    {
      title: '角色', dataIndex: 'role', width: 100,
      render: v => <Tag color={v === 'admin' ? 'red' : 'blue'}>{v === 'admin' ? '管理员' : '主体'}</Tag>
    },
    { title: '关联主体', dataIndex: 'supplier_name', width: 150, render: v => v || '-' },
    {
      title: '批量分配', dataIndex: 'allow_batch_assign', width: 80, align: 'center',
      render: v => <Tag color={v === 1 ? 'green' : 'default'}>{v === 1 ? '允许' : '禁止'}</Tag>
    },
    {
      title: '批量修改', dataIndex: 'allow_batch_edit', width: 80, align: 'center',
      render: v => <Tag color={v === 1 ? 'green' : 'default'}>{v === 1 ? '允许' : '禁止'}</Tag>
    },
    {
      title: '权限模板', dataIndex: 'template_name', width: 150,
      render: (v, record) => record.role === 'supplier' ? '-' : (v || <Tag>未关联</Tag>)
    },
    {
      title: '状态', dataIndex: 'status', width: 80,
      render: v => <Tag color={v === 1 ? 'green' : 'default'}>{v === 1 ? '启用' : '禁用'}</Tag>
    },
    {
      title: '创建时间', dataIndex: 'created_at', width: 160,
      render: v => v ? new Date(v).toLocaleString() : '-'
    },
    {
      title: '操作', key: 'action', width: 500,
      render: (_, record) => (
        <Space>
          {canEdit && <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>}
          {canEdit && <Button type="link" size="small" icon={<SettingOutlined />} onClick={() => handleFieldPerm(record)}>字段权限</Button>}
          {canEdit && <Button type="link" size="small" icon={<MenuOutlined />} onClick={() => handleMenuPerm(record)}>菜单权限</Button>}
          {canEdit && <Button type="link" size="small" icon={<SettingOutlined />} onClick={() => handleDataPerm(record)}>数据权限</Button>}
          {canEdit && <Button type="link" size="small" icon={<SettingOutlined />} onClick={() => handleBillPerm(record)}>账单权限</Button>}
          {canEdit && <Button type="link" size="small" icon={<KeyOutlined />} onClick={() => {
            setResetPwdUser(record);
            resetForm.resetFields();
            setResetPwdVisible(true);
          }}>重置密码</Button>}
          {canEdit && (
            <Popconfirm title="确定删除？" onConfirm={() => handleDelete(record.id)}>
              <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  // 字段权限表格列
  const fieldPermColumns = [
    {
      title: '字段名称',
      dataIndex: 'field_name',
      render: (v) => FIELD_LABELS[v] || v
    },
    {
      title: '字段标识',
      dataIndex: 'field_name',
      render: v => <Tag>{v}</Tag>
    },
    {
      title: '是否可见',
      dataIndex: 'is_visible',
      width: 100,
      render: (v, record) => (
        <Switch
          checked={v}
          size="small"
          onChange={(checked) => handleFieldToggle(record.field_name, 'is_visible', checked)}
        />
      )
    },
    {
      title: '是否可编辑',
      dataIndex: 'is_editable',
      width: 100,
      render: (v, record) => (
        <Switch
          checked={v}
          size="small"
          onChange={(checked) => handleFieldToggle(record.field_name, 'is_editable', checked)}
        />
      )
    }
  ];

  // 菜单权限表格列
  const menuPermColumns = [
    {
      title: '菜单名称',
      dataIndex: 'label',
      render: (v, record) => MENU_LABELS[record.key] || v
    },
    {
      title: '菜单标识',
      dataIndex: 'key',
      render: v => <Tag>{v}</Tag>
    },
    {
      title: '是否可见',
      dataIndex: 'is_visible',
      width: 100,
      render: (v, record) => (
        <Switch
          checked={v === 1}
          size="small"
          onChange={(checked) => handleMenuToggle(record.key, 'is_visible', checked)}
        />
      )
    },
    {
      title: '是否可编辑',
      dataIndex: 'is_editable',
      width: 100,
      render: (v, record) => (
        <Switch
          checked={v === 1}
          size="small"
          disabled={record.is_visible === 0}
          onChange={(checked) => handleMenuToggle(record.key, 'is_editable', checked)}
        />
      )
    }
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <h3>用户管理</h3>
        <Space>
          {canEdit && <Button icon={<ImportOutlined />} onClick={() => navigate('/users/import')}>批量导入</Button>}
          {canEdit && <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>新增用户</Button>}
        </Space>
      </div>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={data}
        loading={loading}
        scroll={{ x: 1400 }}
        pagination={{
          current: page,
          pageSize: 20,
          total,
          showTotal: t => `共 ${t} 条`,
          onChange: p => setPage(p)
        }}
      />

      {/* 新增/编辑弹窗 */}
      <Modal
        title={editingUser ? '编辑用户' : '新增用户'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => setModalVisible(false)}
        confirmLoading={saving}
        width={500}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="username" label="用户名" rules={[{ required: true, message: '请输入用户名' }]}>
            <Input disabled={!!editingUser} />
          </Form.Item>
          {!editingUser && (
            <Form.Item name="password" label="密码" rules={[{ required: true, message: '请输入密码' }, { min: 6, message: '密码至少6位' }]}>
              <Input.Password />
            </Form.Item>
          )}
          <Form.Item name="name" label="显示名称" rules={[{ required: true, message: '请输入显示名称' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="role" label="角色" rules={[{ required: true }]}>
            <Select
              options={[
                { label: '管理员', value: 'admin' },
                { label: '主体', value: 'supplier' }
              ]}
            />
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.role !== cur.role}>
            {({ getFieldValue }) =>
              getFieldValue('role') === 'supplier' && (
                <Form.Item name="supplier_id" label="关联主体" rules={[{ required: true, message: '请选择主体' }]}>
                  <Select
                    options={suppliers.map(s => ({ label: s.name, value: s.id }))}
                    placeholder="请选择主体"
                  />
                </Form.Item>
              )
            }
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.role !== cur.role}>
            {({ getFieldValue }) =>
              getFieldValue('role') === 'admin' && (
                <Form.Item name="template_id" label="权限模板">
                  <Select
                    allowClear
                    placeholder="选择权限模板（可选）"
                    options={templates.map(t => ({ label: t.name, value: t.id }))}
                  />
                </Form.Item>
              )
            }
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select
              options={[
                { label: '启用', value: 1 },
                { label: '禁用', value: 0 }
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 重置密码弹窗 */}
      <Modal
        title={`重置密码 - ${resetPwdUser?.name}`}
        open={resetPwdVisible}
        onOk={handleResetPassword}
        onCancel={() => setResetPwdVisible(false)}
      >
        <Form form={resetForm} layout="vertical">
          <Form.Item name="newPassword" label="新密码" rules={[{ required: true, message: '请输入新密码' }, { min: 6, message: '密码至少6位' }]}>
            <Input.Password />
          </Form.Item>
        </Form>
      </Modal>

      {/* 字段权限配置弹窗 */}
      <Modal
        title={`字段权限配置 - ${fieldPermUser?.name}`}
        open={fieldPermVisible}
        onCancel={() => setFieldPermVisible(false)}
        width={700}
        footer={
          <Space>
            <Button onClick={() => { handleSelectAll('is_visible', true); handleSelectAll('is_editable', true); }}>全部允许</Button>
            <Button onClick={() => handleSelectAll('is_visible', true)}>全部可见</Button>
            <Button onClick={() => handleSelectAll('is_visible', false)}>全部隐藏</Button>
            <Button onClick={() => handleSelectAll('is_editable', false)}>全部只读</Button>
            <Button type="primary" onClick={handleSaveFieldPerm} loading={fieldPermSaving}>保存配置</Button>
          </Space>
        }
      >
        <p style={{ marginBottom: 16, color: '#666' }}>
          配置该用户的订单字段权限。「可见」控制用户能否看到该字段；「可编辑」控制用户能否修改该字段。
        </p>
        <Table
          rowKey="field_name"
          columns={fieldPermColumns}
          dataSource={fieldPermissions}
          loading={fieldPermLoading}
          pagination={false}
          size="small"
        />
      </Modal>

      {/* 菜单权限配置弹窗 */}
      <Modal
        title={`菜单权限配置 - ${menuPermUser?.name}`}
        open={menuPermVisible}
        onCancel={() => setMenuPermVisible(false)}
        width={600}
        footer={
          <Space>
            <Button onClick={() => setMenuPermissions(prev => prev.map(m => ({ ...m, is_visible: 1, is_editable: 1 })))}>全部允许</Button>
            <Button onClick={() => setMenuPermissions(prev => prev.map(m => ({ ...m, is_visible: 1 })))}>全部可见</Button>
            <Button onClick={() => setMenuPermissions(prev => prev.map(m => ({ ...m, is_visible: 0, is_editable: 0 })))}>全部隐藏</Button>
            <Button onClick={() => setMenuPermissions(prev => prev.map(m => ({ ...m, is_editable: 0 })))}>全部只读</Button>
            <Button type="primary" onClick={handleSaveMenuPerm} loading={menuPermSaving}>保存配置</Button>
          </Space>
        }
      >
        <p style={{ marginBottom: 16, color: '#666' }}>
          配置该用户可以看到的菜单项。保存后用户需要重新登录才能生效。
        </p>
        <Table
          rowKey="key"
          columns={menuPermColumns}
          dataSource={menuPermissions}
          loading={menuPermLoading}
          pagination={false}
          size="small"
        />
      </Modal>

      {/* 数据权限配置弹窗 */}
      <Modal
        title={`数据权限配置 - ${dataPermUser?.name}`}
        open={dataPermVisible}
        onCancel={() => setDataPermVisible(false)}
        width={600}
        footer={
          <Space>
            <Button onClick={() => setDataPermVisible(false)}>取消</Button>
            <Button type="primary" onClick={handleSaveDataPerm} loading={dataPermSaving}>保存配置</Button>
          </Space>
        }
      >
        {dataPermLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>加载中...</div>
        ) : (
          <div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 500, marginBottom: 8 }}>允许查看的平台</div>
              <p style={{ color: '#999', marginBottom: 8 }}>不选择任何平台 = 查看所有平台的订单</p>
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                placeholder="选择允许的平台（留空=全部）"
                value={dataPermissions.platforms}
                onChange={v => setDataPermissions(prev => ({ ...prev, platforms: v }))}
              >
                <Select.Option key="__blank__" value="__blank__">（空白）</Select.Option>
                {allPlatforms.map(p => (
                  <Select.Option key={p.name} value={p.name}>{p.name}</Select.Option>
                ))}
              </Select>
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 500, marginBottom: 8 }}>允许查看的小组</div>
              <p style={{ color: '#999', marginBottom: 8 }}>不选择任何小组 = 查看所有小组的订单</p>
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                placeholder="选择允许的小组（留空=全部）"
                value={dataPermissions.teams}
                onChange={v => setDataPermissions(prev => ({ ...prev, teams: v }))}
              >
                <Select.Option key="__blank__" value="__blank__">（空白）</Select.Option>
                {allTeams.map(t => (
                  <Select.Option key={t.name} value={t.name}>{t.name}</Select.Option>
                ))}
              </Select>
            </div>
            <div>
              <div style={{ fontWeight: 500, marginBottom: 8 }}>限制修改的状态</div>
              <p style={{ color: '#999', marginBottom: 8 }}>选中的状态将禁止该用户修改。状态管理中锁定的状态对所有人限制，优先级最高。</p>
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                placeholder="选择要限制的状态（留空=不限制）"
                value={dataPermissions.statuses}
                onChange={v => setDataPermissions(prev => ({ ...prev, statuses: v }))}
              >
                {allStatuses.filter(s => !s.is_locked).map(s => (
                  <Select.Option key={s.id} value={s.id}>{s.name}</Select.Option>
                ))}
              </Select>
            </div>
          </div>
        )}
      </Modal>

      {/* 账单权限配置弹窗 */}
      <Modal
        title={`账单权限 - ${billPermUser?.name}`}
        open={billPermVisible}
        onCancel={() => setBillPermVisible(false)}
        width={600}
        footer={<Space><Button onClick={() => setBillPermVisible(false)}>取消</Button><Button type="primary" onClick={handleSaveBillPerm} loading={billPermSaving}>保存配置</Button></Space>}
      >
        {billPermLoading ? <div style={{ textAlign: 'center', padding: 40 }}>加载中...</div> : (
          <>
            <Space style={{ marginBottom: 12 }}>
              <Button size="small" onClick={() => setBillPermissions(prev => prev.map(p => ({ ...p, is_visible: 1 })))}>全部可见</Button>
              <Button size="small" onClick={() => setBillPermissions(prev => prev.map(p => ({ ...p, is_visible: 0, is_editable: 0 })))}>全部隐藏</Button>
              <Button size="small" onClick={() => setBillPermissions(prev => prev.map(p => ({ ...p, is_editable: 1 })))}>全部可编辑</Button>
              <Button size="small" onClick={() => setBillPermissions(prev => prev.map(p => ({ ...p, is_editable: 0 })))}>全部只读</Button>
            </Space>
            <Table rowKey="field_name" columns={[
            { title: '字段名', dataIndex: 'field_name' },
            { title: '中文名', dataIndex: 'label' },
            { title: '可见', dataIndex: 'is_visible', width: 70, align: 'center',
              render: (v, r) => <Switch checked={v===1} onChange={c => setBillPermissions(prev => prev.map(p => p.field_name===r.field_name ? {...p, is_visible: c?1:0, is_editable: c?p.is_editable:0} : p))} /> },
            { title: '可编辑', dataIndex: 'is_editable', width: 70, align: 'center',
              render: (v, r) => <Switch checked={v===1} onChange={c => setBillPermissions(prev => prev.map(p => p.field_name===r.field_name ? {...p, is_editable: c?1:0} : p))} /> }
          ]} dataSource={billPermissions} pagination={false} size="small" />
          </>
        )}
      </Modal>
    </div>
  );
}

export default UserManage;
